import { useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Chip, Typography, Pagination, Stack,
} from '@mui/material';
import { Button, Card, PageHeader } from 'Components/ui';
import { useTranslation } from 'react-i18next';
import { useAuthTenant } from 'store/auth';
import { getScannerScans, getScannerAccounts } from 'services/scanner-service';
import { Main as Container } from 'Components/Main';

const STATUS_COLORS = {
  executing: 'warning',
  available: 'success',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
};

const BTN = {
  borderRadius: '8px',
  padding: '8px 20px',
  height: '36px',
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
};

export const ScannerScans = () => {
  const { t } = useTranslation('scanner');
  const selectedTenant = useSelector(useAuthTenant);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const STATUS_LABELS = {
    executing: t('scanner:scanStatus.executing'),
    available: t('scanner:scanStatus.available'),
    completed: t('scanner:scanStatus.completed'),
    failed: t('scanner:scanStatus.failed'),
    cancelled: t('scanner:scanStatus.cancelled'),
  };

  const { data, isLoading } = useQuery(
    ['scannerScans', selectedTenant.slug, page],
    () => getScannerScans(selectedTenant.slug, page),
    { enabled: !!selectedTenant.slug },
  );

  const { data: accountsData } = useQuery(
    ['scannerAccounts', selectedTenant.slug],
    () => getScannerAccounts(selectedTenant.slug),
    { enabled: !!selectedTenant.slug },
  );

  const scans = data?.data?.scans || [];
  const totalPages = data?.data?.pagination?.pages;

  const accountMap = (accountsData?.data || []).reduce((acc, account) => {
    if (account.prowlerProviderId) acc[account.prowlerProviderId] = account.name;
    return acc;
  }, {});

  return (
    <Container>
      <PageHeader
        title={t('scanner:scans.title')}
        segments={[
          { label: t('common:breadcrumbs.analysis') },
          { label: t('scanner:dashboard.title'), route: '/scanner/dashboard' },
          { label: t('scanner:scans.title') },
        ]}
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate('/scanner/accounts')}
            sx={{ ...BTN, width: 'fit-content' }}
          >
            {t('scanner:scans.manageAccounts')}
          </Button>
        }
      />
      <Box sx={{ mt: 3, pb: 6 }}>

      {/* Table */}
      <Card elevation={0} sx={{ padding: 0, border: '1px solid var(--vo-line)', borderRadius: 2, overflow: 'hidden' }}>
        {isLoading && (
          <Box sx={{ p: 6, textAlign: 'center', color: 'var(--vo-slate)' }}>{t('scanner:scans.loading')}</Box>
        )}

        {!isLoading && scans.length === 0 && (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <Typography sx={{ color: 'var(--vo-slate)', mb: 2 }}>{t('scanner:scans.noScans')}</Typography>
            <Button
              size="sm"
              variant="primary"
              onClick={() => navigate('/scanner/accounts')}
              sx={BTN}
            >
              {t('scanner:scans.manageAccounts')}
            </Button>
          </Box>
        )}

        {scans.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--vo-mist)' }}>
                  {[
                    t('scanner:scans.colDate'),
                    t('scanner:scans.colAccount'),
                    t('scanner:scans.colStatus'),
                    t('scanner:scans.colDuration'),
                  ].map(col => (
                    <th key={col} style={{ padding: '12px 16px', fontWeight: 600, fontSize: 12, color: 'var(--vo-slate)', textAlign: 'left', borderBottom: '1px solid var(--vo-line)', whiteSpace: 'nowrap' }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scans.map(scan => (
                  <tr
                    key={scan.id}
                    style={{ borderBottom: '1px solid var(--vo-line)', cursor: 'pointer' }}
                    onClick={() => navigate(`/scanner/scans/${scan.id}`)}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--vo-mist)'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                  >
                    <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--vo-ink)' }}>
                      {scan.started_at || scan.scheduled_at
                        ? new Date(scan.started_at || scan.scheduled_at).toLocaleString('pt-BR')
                        : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--vo-ink-2)' }}>
                      {accountMap[scan._rel?.provider?.data?.id] || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', width: 1, whiteSpace: 'nowrap' }}>
                      <Chip
                        label={STATUS_LABELS[scan.state] || scan.state}
                        color={STATUS_COLORS[scan.state] || 'default'}
                        size="small"
                      />
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--vo-slate)' }}>
                      {scan.duration ? t('scanner:scans.duration', { minutes: Math.round(scan.duration / 60) }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack sx={{ justifyContent: 'center', flexDirection: 'row', mt: 3 }}>
          <Typography sx={{ fontSize: 12, color: 'var(--vo-slate)', alignSelf: 'center', mr: 2 }}>
            {t('scanner:scans.page', { current: page, total: totalPages })}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
      </Box>
    </Container>
  );
};
